<!--
  Flexi — HMS-status tab.

  Three bands: the latest published result, the score benchmark, and the full
  history. A business-level viewer gets the same three read-only — no New, no
  re-open, no delete. The grid keeps every column, the kebab menu and the
  paginator it had before.
-->
<div class="fcomp-list">

  <header class="fcomp-list__head">
    <div class="fcomp-list__heading">
      <h2 class="fcomp-list__title">{{ 'FLEXI_COMPLIANCE.TAB' | translate }}</h2>
      <p class="fcomp-list__subtitle">{{ 'FLEXI_COMPLIANCE.LIST.SUBTITLE' | translate }}</p>
    </div>

    <button
      *ngIf="canAuthor"
      class="fcomp-btn fcomp-btn--primary"
      type="button"
      (click)="openStartNewReview()"
    >
      <i class="pi pi-plus" aria-hidden="true"></i>
      <span>{{ 'NEW' | translate }}</span>
    </button>
  </header>

  <div class="alert alert-danger" *ngIf="loadError">{{ loadError }}</div>

  <!-- ═══════════ Latest result + benchmark ═══════════ -->
  <div class="fcomp-top" *ngIf="heroReview || showChart">

    <!-- Hero: the score the customer is living with -->
    <section class="fcomp-hero" *ngIf="heroReview as hero" [ngClass]="toneClass(hero)">
      <div class="fcomp-hero__head">
        <span class="fcomp-hero__eyebrow">{{ 'FLEXI_COMPLIANCE.LIST.LATEST' | translate }}</span>

        <!-- The formula is weighted, counts a half for yellow, counts unrated
             checkpoints against the total and re-rates a checkpoint whose
             recurring commitment has gone overdue. None of that is guessable
             from a percentage, and the last one moves the number with nobody
             editing the review — hence an explainer rather than a tooltip. -->
        <button
          type="button"
          class="fcomp-hero__help"
          (click)="showScoreHelp = true"
          [attr.aria-label]="'FLEXI_COMPLIANCE.SCORE_HELP.OPEN' | translate"
          [pTooltip]="'FLEXI_COMPLIANCE.SCORE_HELP.OPEN' | translate"
          tooltipPosition="right"
        >
          <i class="pi pi-info-circle" aria-hidden="true"></i>
        </button>

        <span class="fcomp-status-pill" [ngClass]="statusClass(hero)">
          {{ statusKey(hero) | translate }}
        </span>
      </div>

      <div class="fcomp-hero__body">
        <div class="fcomp-hero__ring">
          <svg viewBox="0 0 124 124" aria-hidden="true">
            <circle class="fcomp-hero__track" cx="62" cy="62" r="52"></circle>
            <circle
              class="fcomp-hero__fill"
              cx="62"
              cy="62"
              r="52"
              [attr.stroke-dasharray]="heroRingDash"
              transform="rotate(-90 62 62)"
            ></circle>
          </svg>
          <span class="fcomp-hero__score">
            <strong>{{ hero.percentage }}</strong>
            <small>{{ 'FLEXI_COMPLIANCE.LIST.PERCENT_UNIT' | translate }}</small>
          </span>
        </div>

        <div class="fcomp-hero__facts">
          <p class="fcomp-hero__verdict" *ngIf="labelKey(hero)">
            <img class="fcomp-hero__face" [src]="faceImage(hero)" alt="" />
            <span>{{ labelKey(hero) | translate }}</span>
          </p>

          <dl class="fcomp-hero__list">
            <dt>{{ 'FLEXI_COMPLIANCE.LIST.COLUMN_CREATED_ON' | translate }}</dt>
            <dd>{{ hero.createdOn | date: 'dd.MM.yyyy' }}</dd>

            <ng-container *ngIf="hero.periodTo">
              <dt>{{ 'FLEXI_COMPLIANCE.LIST.COLUMN_VALID_TILL' | translate }}</dt>
              <dd>{{ hero.periodTo | date: 'dd.MM.yyyy' }}</dd>
            </ng-container>

            <ng-container *ngIf="hero.completedByName">
              <dt>{{ 'FLEXI_COMPLIANCE.LIST.CHART_COMPLETED_BY' | translate }}</dt>
              <dd>{{ hero.completedByName }}</dd>
            </ng-container>
          </dl>
        </div>
      </div>

      <div class="fcomp-hero__actions">
        <button type="button" class="fcomp-btn fcomp-btn--quiet" (click)="open(hero)">
          <i class="pi" [ngClass]="actionIcon(hero)" aria-hidden="true"></i>
          <span>{{ actionKey(hero) | translate }}</span>
        </button>

        <button
          *ngIf="hero.generatedFeatureManualDocumentId"
          type="button"
          class="fcomp-btn fcomp-btn--neutral"
          (click)="downloadPdf(hero, $event)"
        >
          <i class="pi pi-file-pdf" aria-hidden="true"></i>
          <span>{{ 'FLEXI_COMPLIANCE.LIST.DOWNLOAD_PDF' | translate }}</span>
        </button>
      </div>
    </section>

    <!-- Benchmark chart -->
    <section class="fcomp-benchmark" *ngIf="showChart">
      <header class="fcomp-benchmark__head">
        <div class="fcomp-benchmark__heading">
          <h3 class="fcomp-benchmark__title">{{ 'FLEXI_COMPLIANCE.LIST.CHART_TITLE' | translate }}</h3>
          <p class="fcomp-benchmark__hint">{{ 'FLEXI_COMPLIANCE.LIST.CHART_HINT' | translate }}</p>
        </div>

        <div
          class="fcomp-benchmark__delta"
          *ngIf="baselineDelta !== null"
          [ngClass]="baselineDelta! >= 0 ? 'is-up' : 'is-down'"
        >
          <i class="pi" [ngClass]="baselineDelta! >= 0 ? 'pi-arrow-up-right' : 'pi-arrow-down-right'" aria-hidden="true"></i>
          <strong class="fcomp-benchmark__delta-value">
            {{ baselineDelta! > 0 ? '+' : '' }}{{ baselineDelta }} pp
          </strong>
          <span class="fcomp-benchmark__delta-label">
            {{ 'FLEXI_COMPLIANCE.LIST.CHART_VS_BASELINE' | translate }} {{ baseline }}%
          </span>
        </div>
      </header>

      <div class="fcomp-benchmark__canvas">
        <p-chart type="line" [data]="chartData" [options]="chartOptions" height="240px"></p-chart>
      </div>
    </section>
  </div>

  <!-- ═══════════ History grid ═══════════ -->
  <section class="fcomp-history">
    <header class="fcomp-history__head">
      <h3 class="fcomp-history__title">{{ 'FLEXI_COMPLIANCE.LIST.HISTORY' | translate }}</h3>
      <span class="fcomp-count" *ngIf="visibleReviews.length">
        {{ visibleReviews.length }} {{ 'FLEXI_COMPLIANCE.LIST.RECORD_LABEL' | translate }}
      </span>

      <!-- Active / Archived sub-tabs. Rendered only once something has actually
           been archived, so a business with no archive keeps the single plain
           grid it had before rather than a strip whose second tab can never
           hold anything. -->
      <div
        class="fcomp-history__tabs"
        *ngIf="hasArchivedReviews"
        role="tablist"
        [attr.aria-label]="'FLEXI_COMPLIANCE.LIST.HISTORY' | translate"
      >
        <button
          id="fcomp-history-tab-active"
          type="button"
          role="tab"
          class="fcomp-history__tab"
          [class.is-active]="historyTab === 'active'"
          [attr.aria-selected]="historyTab === 'active'"
          [attr.tabindex]="historyTab === 'active' ? 0 : -1"
          (click)="selectHistoryTab('active')"
        >
          {{ 'FLEXI_COMPLIANCE.LIST.TAB_ACTIVE' | translate }}
          <span class="fcomp-history__tab-count">{{ activeReviews.length }}</span>
        </button>

        <button
          id="fcomp-history-tab-archived"
          type="button"
          role="tab"
          class="fcomp-history__tab"
          [class.is-active]="historyTab === 'archived'"
          [attr.aria-selected]="historyTab === 'archived'"
          [attr.tabindex]="historyTab === 'archived' ? 0 : -1"
          (click)="selectHistoryTab('archived')"
        >
          {{ 'FLEXI_COMPLIANCE.LIST.TAB_ARCHIVED' | translate }}
          <span class="fcomp-history__tab-count">{{ archivedReviews.length }}</span>
        </button>
      </div>
    </header>

    <!-- The grid is the tab panel, and says so only while the strip above it
         exists — a lone tabpanel with no tablist is worse than no ARIA at all. -->
    <p-table
      [value]="visibleReviews"
      [attr.role]="hasArchivedReviews ? 'tabpanel' : null"
      [attr.aria-labelledby]="hasArchivedReviews ? 'fcomp-history-tab-' + historyTab : null"
      dataKey="id"
      [loading]="loading"
      [rows]="10"
      [paginator]="true"
      [pageLinks]="3"
      [rowsPerPageOptions]="[5, 10, 20]"
      paginatorDropdownAppendTo="body"
      [showCurrentPageReport]="true"
      [rowHover]="true"
      responsiveLayout="scroll"
      styleClass="p-datatable-sm fcomp-modern-table"
      currentPageReportTemplate="{{ 'SUMMARY_TOTALRECORDS' | translate }}"
    >
      <ng-template pTemplate="header">
        <tr>
          <th style="min-width: 160px">{{ 'FLEXI_COMPLIANCE.LIST.COLUMN_CREATED_ON' | translate }}</th>
          <th style="min-width: 130px">{{ 'FLEXI_COMPLIANCE.LIST.COLUMN_VALID_TILL' | translate }}</th>
          <th style="width: 130px">{{ 'FLEXI_COMPLIANCE.LIST.COLUMN_SCORE' | translate }}</th>
          <th style="min-width: 160px">{{ 'FLEXI_COMPLIANCE.LIST.COLUMN_CREATED_BY' | translate }}</th>
          <th style="min-width: 160px">{{ 'FLEXI_COMPLIANCE.LIST.COLUMN_MODIFIED_BY' | translate }}</th>
          <th style="width: 140px">{{ 'FLEXI_COMPLIANCE.LIST.COLUMN_STATUS' | translate }}</th>
          <th style="width: 90px; text-align: right">{{ 'ACTIONS' | translate }}</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-review>
        <tr>
          <td>
            <a class="fcomp-date-link" href="javascript:void(0)" (click)="open(review)">
              {{ review.createdOn | date: 'dd.MM.yyyy HH:mm' }}
            </a>
          </td>
          <td>
            <span *ngIf="review.periodTo; else noPeriod" class="fcomp-num">
              {{ review.periodTo | date: 'dd.MM.yyyy' }}
            </span>
            <ng-template #noPeriod><span class="fcomp-empty-cell">—</span></ng-template>
          </td>
          <td>
            <span
              class="fcomp-score-pill"
              *ngIf="review.percentage !== null; else noScore"
              [ngClass]="toneClass(review)"
            >
              <i
                class="pi"
                [ngClass]="{
                  'pi-face-smile': review.smiley === 'green',
                  'pi-minus-circle': review.smiley === 'yellow',
                  'pi-times-circle': review.smiley === 'red'
                }"
                aria-hidden="true"
              ></i>
              {{ review.percentage }}%
            </span>
            <ng-template #noScore><span class="fcomp-empty-cell">—</span></ng-template>
          </td>
          <td>
            <span *ngIf="review.createdByName; else noCreatedBy">{{ review.createdByName }}</span>
            <ng-template #noCreatedBy><span class="fcomp-empty-cell">—</span></ng-template>
          </td>
          <td>
            <span *ngIf="review.modifiedByName; else noModifiedBy">{{ review.modifiedByName }}</span>
            <ng-template #noModifiedBy><span class="fcomp-empty-cell">—</span></ng-template>
          </td>
          <td>
            <span class="fcomp-status-pill" [ngClass]="statusClass(review)">
              {{ statusKey(review) | translate }}
            </span>
          </td>
          <td>
            <!-- Single kebab trigger, matching the SDS/Chemicals grid. Every
                 row action lives in the popup, so the column stays one icon wide
                 however many actions a given row happens to support. -->
            <div class="fcomp-row-actions">
              <button
                type="button"
                class="fcomp-actions-trigger"
                (click)="openActionsMenu($event, review)"
                [attr.aria-label]="'ACTIONS' | translate"
                [pTooltip]="'ACTIONS' | translate"
                tooltipPosition="left"
              >
                <i class="pi" [ngClass]="busyReviewIds.has(review.id) ? 'pi-spin pi-spinner' : 'pi-ellipsis-v'"></i>
              </button>
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7">
            <!-- Standing in the archive, "start the first review" offers to create
                 something that would not appear here — so the archived tab states
                 the fact and stops. -->
            <div class="fcomp-empty" *ngIf="historyTab === 'archived'; else activeEmpty">
              <span class="fcomp-empty__icon" aria-hidden="true"><i class="pi pi-folder-open"></i></span>
              <p class="fcomp-empty__title">
                {{ 'FLEXI_COMPLIANCE.LIST.EMPTY_ARCHIVED' | translate }}
              </p>
            </div>

            <!-- The author's empty state points at the New button, which a viewer
                 does not have — so they get the plain statement instead. -->
            <ng-template #activeEmpty>
              <div class="fcomp-empty">
                <span class="fcomp-empty__icon" aria-hidden="true"><i class="pi pi-inbox"></i></span>
                <p class="fcomp-empty__title">
                  {{ (canAuthor ? 'FLEXI_COMPLIANCE.LIST.EMPTY' : 'FLEXI_COMPLIANCE.LIST.EMPTY_VIEWER') | translate }}
                </p>
                <p class="fcomp-empty__hint" *ngIf="canAuthor">
                  {{ 'FLEXI_COMPLIANCE.LIST.EMPTY_HINT' | translate }}
                </p>
                <button
                  *ngIf="canAuthor"
                  type="button"
                  class="fcomp-btn fcomp-btn--primary"
                  (click)="openStartNewReview()"
                >
                  <i class="pi pi-plus" aria-hidden="true"></i>
                  <span>{{ 'FLEXI_COMPLIANCE.START_NEW.START' | translate }}</span>
                </button>
              </div>
            </ng-template>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </section>
</div>

<!-- Shared row-actions dropdown: one instance, model set per row on open.
     appendTo="body" so the table's own overflow cannot clip it. -->
<p-menu
  #rowActionsMenu
  [popup]="true"
  [model]="actionMenuItems"
  appendTo="body"
  styleClass="fcomp-actions-menu"
></p-menu>

<!-- ═══════════ How the score is calculated ═══════════ -->
<p-dialog
  [(visible)]="showScoreHelp"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '560px', maxWidth: '95vw' }"
  [header]="'FLEXI_COMPLIANCE.SCORE_HELP.TITLE' | translate"
  styleClass="fcomp-dialog">

  <p class="fcomp-score-help__intro">
    {{ 'FLEXI_COMPLIANCE.SCORE_HELP.INTRO' | translate }}
  </p>

  <ul class="fcomp-score-help__rules">
    <li>{{ 'FLEXI_COMPLIANCE.SCORE_HELP.RULE_WEIGHTED' | translate }}</li>
    <li>{{ 'FLEXI_COMPLIANCE.SCORE_HELP.RULE_RATINGS' | translate }}</li>
    <li>{{ 'FLEXI_COMPLIANCE.SCORE_HELP.RULE_UNRATED' | translate }}</li>
    <li>{{ 'FLEXI_COMPLIANCE.SCORE_HELP.RULE_REMOVED' | translate }}</li>
    <li>{{ 'FLEXI_COMPLIANCE.SCORE_HELP.RULE_OVERDUE' | translate }}</li>
  </ul>

  <h4 class="fcomp-score-help__bands-title">
    {{ 'FLEXI_COMPLIANCE.SCORE_HELP.BANDS_TITLE' | translate }}
  </h4>

  <!-- Bounds come from the component's constants, not the copy — see scoreBands. -->
  <ul class="fcomp-score-help__bands">
    <li>
      <span class="fcomp-score-pill is-red">
        <i class="pi pi-times-circle" aria-hidden="true"></i>
        {{ 'FLEXI_COMPLIANCE.SCORE_CHIP.RED_LABEL' | translate }}
      </span>
      <span>{{ 'FLEXI_COMPLIANCE.SCORE_HELP.BAND_RED' | translate:scoreBands }}</span>
    </li>
    <li>
      <span class="fcomp-score-pill is-yellow">
        <i class="pi pi-minus-circle" aria-hidden="true"></i>
        {{ 'FLEXI_COMPLIANCE.SCORE_CHIP.YELLOW_LABEL' | translate }}
      </span>
      <span>{{ 'FLEXI_COMPLIANCE.SCORE_HELP.BAND_YELLOW' | translate:scoreBands }}</span>
    </li>
    <li>
      <span class="fcomp-score-pill is-green">
        <i class="pi pi-face-smile" aria-hidden="true"></i>
        {{ 'FLEXI_COMPLIANCE.SCORE_CHIP.GREEN_LABEL' | translate }}
      </span>
      <span>{{ 'FLEXI_COMPLIANCE.SCORE_HELP.BAND_GREEN' | translate:scoreBands }}</span>
    </li>
  </ul>

  <ng-template pTemplate="footer">
    <div class="fcomp-dialog__foot">
      <button type="button" class="fcomp-btn fcomp-btn--neutral" (click)="showScoreHelp = false">
        {{ 'CLOSE' | translate }}
      </button>
    </div>
  </ng-template>
</p-dialog>

<!-- ═══════════ New review — template picker ═══════════ -->
<p-dialog
  [(visible)]="showStartNewDialog"
  [modal]="true"
  [closable]="!startingNewReview"
  [draggable]="false"
  [style]="{ width: '560px', maxWidth: '95vw' }"
  [header]="'FLEXI_COMPLIANCE.START_NEW.TITLE' | translate"
  styleClass="fcomp-dialog"
  (onHide)="closeStartNewReview()">

  <p class="fcomp-start-new__intro">
    {{ 'FLEXI_COMPLIANCE.START_NEW.INTRO' | translate }}
  </p>

  <!-- What the sweep will do, before it is agreed to. Rendered only when there
       is actually something to lose: a business with no outstanding
       commitments would otherwise be warned about nothing. -->
  <div class="fcomp-start-new__warning"
       *ngIf="!outstandingLoading && outstandingActionPoints">
    <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
    <span>{{ 'FLEXI_COMPLIANCE.START_NEW.REJECTS_OUTSTANDING'
              | translate:{ count: outstandingActionPoints } }}</span>
  </div>

  <div class="alert alert-danger" *ngIf="pickerError">{{ pickerError }}</div>

  <div *ngIf="pickerLoading" class="fcomp-start-new__loading">
    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
    {{ 'FLEXI_COMPLIANCE.LOADING' | translate }}
  </div>

  <ul class="fcomp-start-new__list" *ngIf="!pickerLoading && pickerTemplates.length > 0">
    <li *ngFor="let t of pickerTemplates"
        class="fcomp-start-new__row"
        [class.is-selected]="selectedTemplateId === t.id"
        (click)="selectedTemplateId = t.id">
      <input type="radio"
             name="newReviewTemplate"
             [value]="t.id"
             [(ngModel)]="selectedTemplateId" />
      <div class="fcomp-start-new__row-body">
        <div class="fcomp-start-new__name">
          {{ t.name }}
          <span class="fcomp-start-new__current" *ngIf="t.isCurrentDefault">
            ({{ 'FLEXI_COMPLIANCE.START_NEW.CURRENT_DEFAULT' | translate }})
          </span>
        </div>
        <div class="fcomp-start-new__desc" *ngIf="t.description">{{ t.description }}</div>
        <div class="fcomp-start-new__catalog">{{ t.catalogVersionCode }}</div>
      </div>
    </li>
  </ul>

  <div *ngIf="!pickerLoading && pickerTemplates.length === 0 && !pickerError"
       class="fcomp-start-new__empty">
    {{ 'FLEXI_COMPLIANCE.START_NEW.NO_TEMPLATES' | translate }}
    <button type="button" class="fcomp-btn fcomp-btn--neutral" (click)="goCreateTemplate()">
      {{ 'FLEXI_COMPLIANCE.START_NEW.CREATE_NEW' | translate }}
    </button>
  </div>

  <ng-template pTemplate="footer">
    <div class="fcomp-dialog__foot">
      <button type="button" class="fcomp-btn fcomp-btn--neutral"
              [disabled]="startingNewReview" (click)="closeStartNewReview()">
        {{ 'CANCEL' | translate }}
      </button>
      <button type="button" class="fcomp-btn fcomp-btn--primary"
              [disabled]="startingNewReview || selectedTemplateId === null"
              (click)="confirmStartNewReview()">
        <i class="pi" [ngClass]="startingNewReview ? 'pi-spin pi-spinner' : 'pi-check'" aria-hidden="true"></i>
        {{ 'FLEXI_COMPLIANCE.START_NEW.START' | translate }}
      </button>
    </div>
  </ng-template>
</p-dialog>

<p-confirmDialog
  [header]="'CONFIRMATION' | translate"
  icon="pi pi-exclamation-triangle"
  [style]="{ width: '480px' }"
  [acceptLabel]="'SWITCH_YES' | translate"
  [rejectLabel]="'SWITCH_NO' | translate">
</p-confirmDialog>
